import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api";

interface ProfileAnalytics {
  profile_views: number;
  extras: number;
  search_appearances: number;
  recruiter_contacts: number;
  keywords_matched: string[];
  top_skills: string[];
  profile_strength: number;
  basic_information: number;
  skills_experience: number;
  professional_summary: number;
  verification_status: {
    id_verified: boolean;
    background_check: boolean;
    skill_assessment: boolean;
  };
  recent_activity: Array<{
    type: string;
    company: string;
    date: string;
    time_ago: string;
  }>;
  profile_view_trends: Array<{
    date: string;
    views: number;
  }>;
  top_search_keywords: Array<{
    keyword: string;
    count: number;
  }>;
  top_searched_job_titles: Array<{
    job_title: string;
    search_count: number;
  }>;
}

const CandidateAnalytics: React.FC = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState<ProfileAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await apiService.getMyCandidateAnalytics();
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadAnalytics}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          No analytics data available
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          My Profile Analytics
        </h1>
        <p className="mt-2 text-gray-600">
          Detailed insights into your profile performance and visibility
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path
                    fillRule="evenodd"
                    d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Profile Views</p>
              <p className="text-2xl font-semibold text-gray-900">
                {analytics.profile_views}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Search Appearances
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {analytics.search_appearances}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Recruiter Contacts
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {analytics.recruiter_contacts}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-indigo-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Profile Strength
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {analytics.profile_strength}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile View Trends */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Profile View Trends (Last 30 Days)
          </h3>
          {analytics.profile_view_trends &&
          analytics.profile_view_trends.length > 0 ? (
            <div className="space-y-2">
              {analytics.profile_view_trends.slice(-7).map((trend, index) => (
                <div
                  key={trend.date}
                  className="flex items-center justify-between"
                >
                  <span className="text-sm text-gray-600">
                    {new Date(trend.date).toLocaleDateString()}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            100,
                            (trend.views /
                              Math.max(
                                ...analytics.profile_view_trends.map(
                                  (t) => t.views,
                                ),
                              )) *
                              100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700 w-8 text-right">
                      {trend.views}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No profile views yet
            </p>
          )}
        </div>

        {/* Top Search Keywords */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Search Keywords That Found You
          </h3>
          {analytics.top_search_keywords &&
          analytics.top_search_keywords.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_search_keywords.map((item, index) => (
                <div
                  key={item.keyword}
                  className="flex items-center justify-between"
                >
                  <span className="text-gray-700 font-medium">
                    {item.keyword}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-emerald-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            100,
                            (item.count /
                              analytics.top_search_keywords[0].count) *
                              100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {item.count} searches
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No search data available yet
            </p>
          )}
        </div>

        {/* Top Job Titles HR Are Searching */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Top Job Titles HR Are Searching
          </h3>
          {analytics.top_searched_job_titles &&
          analytics.top_searched_job_titles.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_searched_job_titles.map((item, index) => (
                <div
                  key={item.job_title}
                  className="flex items-center justify-between"
                >
                  <span className="text-gray-700 font-medium">
                    {item.job_title}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            100,
                            (item.search_count /
                              analytics.top_searched_job_titles[0]
                                .search_count) *
                              100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {item.search_count} searches
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No job title search data available yet
            </p>
          )}
        </div>

        {/* AI Keywords Matched */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            AI-Extracted Keywords
          </h3>
          <div className="space-y-3">
            {analytics.keywords_matched.map((keyword, index) => (
              <div key={keyword} className="flex items-center justify-between">
                <span className="text-gray-700">{keyword}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-emerald-600 h-2 rounded-full"
                      style={{ width: `${Math.max(20, 100 - index * 15)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-500">AI match</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Profile Completeness */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Profile Completeness
          </h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Overall Progress</span>
              <span>{analytics.profile_strength}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${analytics.profile_strength}%` }}
              ></div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Basic Info & Education</span>
              <span className="font-semibold text-gray-900">
                {analytics.basic_information}%
              </span>
            </div>

            <div className="flex items-center justify-between text-sm mt-4">
              <span className="text-gray-600">Skills & Experience</span>
              <span className="font-semibold text-gray-900">
                {analytics.skills_experience}%
              </span>
            </div>

            <div className="flex items-center justify-between text-sm mt-4">
              <span className="text-gray-600">Professional Summary</span>
              <span className="font-semibold text-gray-900">
                {analytics.professional_summary}%
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-4">
              <span className="text-gray-600">Extras(Projects, Certifications & Languages)</span>
              <span className="font-semibold text-gray-900">
                {analytics.extras}%
              </span>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            {analytics.recent_activity.length > 0 ? (
              analytics.recent_activity.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activity.type === "Profile View"
                          ? "bg-blue-100"
                          : "bg-emerald-100"
                      }`}
                    >
                      {activity.type === "Profile View" ? (
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path
                            fillRule="evenodd"
                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4 text-emerald-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      {activity.type} by{" "}
                      <span className="font-medium">{activity.company}</span>
                    </p>
                    <p className="text-xs text-gray-500">{activity.time_ago}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">
                No recent activity
              </p>
            )}
          </div>
        </div>

        {/* Verification Services */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Verification Services
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span className="text-gray-700">ID Verification</span>
              </div>
              <Link
                to="/candidate/profile-boost"
                className="bg-emerald-600 text-white px-3 py-1 rounded-md text-sm hover:bg-emerald-700"
              >
                Verify Now
              </Link>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span className="text-gray-700">Background Check</span>
              </div>
              <button
                type="button"
                disabled
                aria-disabled="true"
                className="bg-gray-200 text-gray-500 px-3 py-1 rounded-md text-sm cursor-not-allowed"
              >
                Start Check
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span className="text-gray-700">Skill Assessment</span>
              </div>
              <button
                type="button"
                disabled
                aria-disabled="true"
                className="bg-gray-200 text-gray-500 px-3 py-1 rounded-md text-sm cursor-not-allowed"
              >
                Take Test
              </button>
            </div>
          </div>

          <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
            <p className="text-sm text-emerald-800">
              <span className="font-medium">Premium Feature:</span> Verified
              candidates get 3x more visibility and appear in premium search
              filters for recruiters.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateAnalytics;
